import { useTranslation } from "@privacy/translations";
import { type FC } from "react";
import { useForm } from "react-hook-form";
import { CircularProgress } from "@privacy/ui";
import * as FormDialog from "../../../../shared/FormDialog";
import { UserSelect } from "../../../../shared/UserSelect";
import { useGetUsersFromCurrentOrganisation } from "../../../../api/users";
import { useAddInvitee } from "../../../../api/registrations";
import type { AddInviteePayload } from "../../../../api/registrations.types";
import { trackEvent } from "../../../../shared/Snowplow";

type AddInviteeDialogProps = {
  onClose: () => void;
  registrationId: string;
  userIdsToExclude: string[];
};

export const AddInviteeDialog: FC<AddInviteeDialogProps> = ({ onClose, registrationId, userIdsToExclude }) => {
  const { t } = useTranslation();

  const { mutate: addInvitee, isError, isPending: isSubmitting } = useAddInvitee(registrationId);

  const { handleSubmit, resetField, control, setValue } = useForm<AddInviteePayload>({
    defaultValues: { userId: undefined, message: t("AddInvitee.Dialog.Form.DefaultMessage") },
  });

  const { data } = useGetUsersFromCurrentOrganisation();

  if (!data) return <CircularProgress />;

  const filteredUser = data.users.filter(user => !userIdsToExclude.includes(user.userId));

  const handleAddInvitee = ({ userId, message }: AddInviteePayload) => {
    addInvitee(
      { userId, message },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  const handleSelectUser = (userId: string | undefined) => {
    if (userId) setValue("userId", userId, { shouldValidate: true });
    else resetField("userId");
  };

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("AddInvitee.Dialog.Header")}</FormDialog.Title>

      <FormDialog.Form
        onSubmit={() => {
          trackEvent.inviteeRegistrationClick();
          handleSubmit(handleAddInvitee)();
        }}
      >
        <FormDialog.Content>
          <FormDialog.FormError isError={isError} />
          <FormDialog.Fields>
            <FormDialog.ControlledField
              control={control}
              name="userId"
              rules={{ required: t("Input.Validation.RequiredEmail") }}
              label={t("AddInvitee.Dialog.Form.PersonLabel")}
            >
              {({ field: { value, ref, onBlur }, fieldState, id }) => (
                <UserSelect
                  enableCreateNewUser
                  id={id}
                  error={Boolean(fieldState.error)}
                  selectedId={value}
                  ref={ref}
                  onBlur={onBlur}
                  users={filteredUser}
                  onSelectUser={handleSelectUser}
                />
              )}
            </FormDialog.ControlledField>

            <FormDialog.TextField
              control={control}
              name="message"
              rules={{ required: t("AddInvitee.Dialog.Form.MessageError") }}
              label={t("AddInvitee.Dialog.Form.MessageLabel")}
              multiline
              minRows={6}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={isSubmitting}>
            {t("AddInvitee.Dialog.Form.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
